import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  selectIsExpandedAiChats,
  selectIsCreateActive,
  setIsCreateActive,
  toggleExpandMode,
  setExpandMode,
  selectSelectedReplyText,
  setSelectedReplyText,
  setIssuesPanelOpen,
} from './agentsHomeReducer';
import { useIsAgentsPanelOpen } from './useIsAgentsPanelOpen';
import { useOpenAgentsPanel } from './useOpenAgentsPanel';
import { useCloseAgentsPanel } from './useCloseAgentsPanel';
import { useArtifactsOptional } from '../common/artifacts/useArtifacts';
import { useTableArtifactsOptional } from '../common/artifacts/useTableArtifacts';

const useAgentsHome = () => {
  const isExpanded = useSelector(selectIsExpandedAiChats);
  const isCreateAgentActive = useSelector(selectIsCreateActive);
  const selectedReplyText = useSelector(selectSelectedReplyText);

  const isOpened = useIsAgentsPanelOpen();
  const openAgentsPanel = useOpenAgentsPanel();
  const closeAgentsPanel = useCloseAgentsPanel();

  const artifactContext = useArtifactsOptional();
  const tableArtifactContext = useTableArtifactsOptional();

  const dispatch = useDispatch();

  const toggleIsExpanded = useCallback(() => {
    dispatch(toggleExpandMode());
  }, [dispatch]);

  const setReplyText = useCallback(
    (text: string | null) => {
      dispatch(setSelectedReplyText(text));
    },
    [dispatch],
  );

  const setIsExpanded = useCallback(
    ({ isExpanded }: { isExpanded: boolean }) => {
      dispatch(setExpandMode({ isExpanded }));
    },
    [dispatch],
  );

  const openIssuesPanel = useCallback(() => {
    dispatch(setIssuesPanelOpen(true));
  }, [dispatch]);

  const closeIssuesPanel = useCallback(() => {
    dispatch(setIssuesPanelOpen(false));
  }, [dispatch]);

  const closeAllPanels = useCallback(() => {
    artifactContext?.closeArtifactPanel?.();
    tableArtifactContext?.closeTablePanel?.();
    closeIssuesPanel();
  }, [artifactContext, tableArtifactContext, closeIssuesPanel]);

  const setIsAgentCreateActive = useCallback(
    (isActive: boolean) => {
      dispatch(setIsCreateActive({ isActive }));
      // When activating agent creation, close the panel
      if (isActive && isOpened) {
        closeAgentsPanel();
      }
    },
    [dispatch, isOpened, closeAgentsPanel],
  );

  return {
    isExpanded,
    toggleIsExpanded,
    openAgentsPanel,
    isOpened,
    setIsAgentCreateActive,
    isCreateAgentActive,
    closeAgentsPanel,
    setIsExpanded,
    selectedReplyText,
    setReplyText,
    closeAllPanels,
    openIssuesPanel,
    closeIssuesPanel,
  };
};

export default useAgentsHome;
